/**
 * routes.tsx - Mobile-first responsive shell and routing with deep link URL patterns.
 *
 * Defines all application routes for the campus board pivot including:
 * - Feed/browse routes with category filtering
 * - Post creation and detail routes
 * - User profile and settings
 * - Inquiry management
 * - Notifications
 * - Saved posts
 * - Admin routes (wrapped in AdminGuard + AdminLayout, lazy-loaded)
 * - Informational pages
 *
 * Deep link patterns support direct navigation from LINE shares and bookmarks.
 *
 * Requirements: 22.1, 22.2, 22.5, 25.3, 1.4, 1.6, NF.1
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { AdminGuard } from './components/AdminGuard';
import { AuthGuard } from './components/AuthGuard';
import { TosGate } from '@/components/TosGate';
import { LoadingState } from './components/LoadingState';

// Lazy-loaded pages for code splitting
const FeedPage = lazy(() => import('./pages/FeedPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const PostCreatePage = lazy(() => import('./pages/PostCreatePage'));
const PostDetailPage = lazy(() => import('./pages/PostDetailPage'));
const MyPostsPage = lazy(() => import('./pages/MyPostsPage'));
const SavedPostsPage = lazy(() => import('./pages/SavedPostsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const InquiryListPage = lazy(() => import('./pages/InquiryListPage'));
const InquiryDetailPage = lazy(() => import('./pages/InquiryDetailPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const TosAcceptancePage = lazy(() => import('./pages/TosAcceptancePage'));

// Admin layout and pages (lazy-loaded for code splitting — NF.1)
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const AdminPostsPage = lazy(() => import('./pages/admin/AdminPostsPage'));
const AdminPostDetailPage = lazy(() => import('./pages/admin/AdminPostDetailPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminUserDetailPage = lazy(() => import('./pages/admin/AdminUserDetailPage'));
const AdminModerationPage = lazy(() => import('./pages/admin/AdminModerationPage'));
const AdminReportsPage = lazy(() =>
  import('./pages/admin/AdminReportsPage').then((m) => ({ default: m.AdminReportsPage })),
);
const ReportDetailPage = lazy(() =>
  import('./pages/admin/ReportDetailPage').then((m) => ({ default: m.ReportDetailPage })),
);
const AdminBugReportsPage = lazy(() =>
  import('./pages/admin/AdminBugReportsPage').then((m) => ({ default: m.AdminBugReportsPage })),
);

// Info pages (named exports wrapped for lazy)
const AboutPage = lazy(() =>
  import('./pages/info/AboutPage').then((m) => ({ default: m.AboutPage })),
);
const SafetyTipsPage = lazy(() =>
  import('./pages/info/SafetyTipsPage').then((m) => ({ default: m.SafetyTipsPage })),
);
const TermsPage = lazy(() =>
  import('./pages/info/TermsPage').then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import('./pages/info/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const HelpSectionPage = lazy(() => import('./pages/help/HelpSectionPage'));
const HelpFaqPage = lazy(() =>
  import('./pages/help/FaqPage').then((m) => ({ default: m.FaqPage })),
);
const HelpBugReportPage = lazy(() =>
  import('./pages/help/BugReportPage').then((m) => ({ default: m.BugReportPage })),
);
const HelpContactPage = lazy(() => import('./pages/help/ContactPage'));
const NotFoundPage = lazy(() =>
  import('./pages/info/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

function PageSuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

export function AppRoutes() {
  return (
    <PageSuspense>
      <Routes>
        {/* Admin routes — wrapped in AdminGuard + AdminLayout (Requirements: 1.4, 1.6, NF.1) */}
        <Route
          path="/admin/*"
          element={
            <AdminGuard>
              <AdminLayout />
            </AdminGuard>
          }
        >
          <Route index element={<Navigate to="/admin/posts" replace />} />
          <Route path="posts" element={<AdminPostsPage />} />
          <Route path="posts/:id" element={<AdminPostDetailPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:id" element={<AdminUserDetailPage />} />
          <Route path="moderation" element={<AdminModerationPage />} />
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="reports/:reportId" element={<ReportDetailPage />} />
          <Route path="bug-reports" element={<AdminBugReportsPage />} />
        </Route>

        {/* User-facing routes — wrapped in AppLayout */}
        <Route
          path="*"
          element={
            <AppLayout>
              <TosGate>
                <Routes>
                {/* Main feed - campus board home */}
                <Route path="/" element={<FeedPage />} />

                {/* Auth callback */}
                <Route path="/auth/callback" element={<AuthCallbackPage />} />

                {/* ToS acceptance - requires auth but bypasses TosGate (allowlisted) */}
                <Route path="/tos/accept" element={<AuthGuard><TosAcceptancePage /></AuthGuard>} />

                {/* Search and browse with filters */}
                <Route path="/search" element={<SearchPage />} />
                <Route path="/browse" element={<SearchPage />} />
                <Route path="/browse/:category" element={<SearchPage />} />

                {/* Post routes */}
                <Route path="/posts/new" element={<AuthGuard><PostCreatePage /></AuthGuard>} />
                <Route path="/posts/:postId" element={<PostDetailPage />} />
                <Route path="/posts/:postId/edit" element={<AuthGuard><PostCreatePage /></AuthGuard>} />

                {/* Legacy listing routes - redirect support */}
                <Route path="/post" element={<AuthGuard><PostCreatePage /></AuthGuard>} />
                <Route path="/listings/:id" element={<PostDetailPage />} />

                {/* User posts and drafts */}
                <Route path="/my-posts" element={<MyPostsPage />} />
                <Route path="/my-listings" element={<MyPostsPage />} />

                {/* Saved posts */}
                <Route path="/saved" element={<SavedPostsPage />} />

                {/* Inquiry management */}
                <Route path="/inquiries" element={<InquiryListPage />} />
                <Route path="/inquiries/:inquiryId" element={<InquiryDetailPage />} />

                {/* Notifications */}
                <Route path="/notifications" element={<NotificationsPage />} />

                {/* Profile and settings */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/settings" element={<ProfilePage />} />
                <Route path="/users/:userId" element={<ProfilePage />} />

                {/* Help and Support */}
                <Route path="/help" element={<HelpSectionPage />} />
                <Route path="/help/faq" element={<HelpFaqPage />} />
                <Route path="/help/bug-report" element={<AuthGuard><HelpBugReportPage /></AuthGuard>} />
                <Route path="/help/contact" element={<AuthGuard><HelpContactPage /></AuthGuard>} />

                {/* Informational pages */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/safety" element={<SafetyTipsPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/faq" element={<Navigate to="/help/faq" replace />} />

                {/* 404 catch-all */}
                <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </TosGate>
            </AppLayout>
          }
        />
      </Routes>
    </PageSuspense>
  );
}
